import { useContext, useMemo, useRef } from "react"
import {
  CreditCardOutlined,
  Close as CloseIcon,
} from "@mui/icons-material"
import { Box, CircularProgress, IconButton, useMediaQuery, makeStyles } from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import Container from "../Elements/Container"
import TopNav from "../common/TopNav"
import LoadingComponentBlur from "../common/LoadingComponentBlur"
import ModalAffirm from "./ModalAffirm"
import LeftPanel from "./LeftPanel"
import RightPanel from "./RightPanel"
import UserContext from "../../context/UserContext"
import useRemainingViewportHeight from "../../hooks/useRemainingViewportHeight"
import {
  metersUsageBarSx,
  metersUsageFillSx,
  metersUsageTrackSx,
  planBadgeSx,
  planInfoContentSx,
  planInfoValueSx,
  topNavMetricsCardSx,
  topNavSummaryCardSx,
  themePalette,
} from "./styles"
import { renderAssignmentNodeIcon, renderCloudAccessBadge, renderStatusIcon } from "./deviceVisuals"
import { useDevicesSettingsData } from "./useDevicesSettingsData"

const useStyles = makeStyles(() => ({
  alertWrapper: {
    position: "fixed",
    top: 0,
    left: "65px",
    width: "calc(100% - 65px)",
    zIndex: 1100,
    pointerEvents: "none",
    "@media (max-width: 920px)": {
      left: 0,
      width: "100%",
      top: 0,
    },
  },
  alertInline: {
    width: "100%",
    margin: 0,
    boxSizing: "border-box",
    borderRadius: 0,
    pointerEvents: "auto",
  },
  initialLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: themePalette.surface.page,
  },
  contentArea: {
    display: "flex",
    backgroundColor: themePalette.surface.page,
    overflow: "hidden",
    minHeight: 0,
  },
  topNavSummaryWrapper: {
    "@media (max-width: 920px)": {
      marginTop: "0.4rem",
    },
  },
}))

// Pantalla orquestadora.
// Mantiene el layout y delega la logica pesada al hook y las piezas visuales a subcomponentes.
export default function DevicesSettings() {
  const classes = useStyles()
  const { plan } = useContext(UserContext)
  const isMobile = useMediaQuery("(max-width:900px)")
  const leftPanelScrollRef = useRef(null)
  const topNavRef = useRef(null)
  const currentPlanLabel = plan?.info?.plan || "-"
  const { remainingHeight: contentViewportHeight, anchorBottom: topNavBottom } = useRemainingViewportHeight(topNavRef)
  const {
    selectedMeter,
    setSelectedMeter,
    editableName,
    setEditableName,
    searchTerm,
    setSearchTerm,
    affirmModalState,
    selectedFilters,
    filtersAnchorEl,
    mobileView,
    setMobileView,
    close,
    setClose,
    msg,
    initialLoading,
    loadingSave,
    loadingBlurState,
    loadingStateChange,
    rightPanelLoading,
    expandedMeters,
    setExpandedMeters,
    activeFilterCount,
    isFilteringDevices,
    activeMetersCount,
    filterSections,
    filteredDevices,
    selectedIsChild,
    selectedCloudSource,
    selectedHasChildren,
    selectedCloudButtonDisabled,
    shouldShowCloudActionButton,
    affirmModalCopy,
    handleOpenFilters,
    handleCloseFilters,
    toggleFilterValue,
    clearAllFilters,
    handleNameBlur,
    handleMeterStatusChange,
    handleCloudAccessAction,
    openAffirmModal,
    closeAffirmModal,
    handleAffirmAction,
  } = useDevicesSettingsData()

  const filterPopoverPaperStyle = useMemo(
    () =>
      isMobile
        ? {
          width: "auto",
          height: "auto",
          maxWidth: "none",
          maxHeight: "none",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          marginTop: 0,
          borderRadius: 0,
          border: "none",
          boxShadow: themePalette.shadow.dialogStrong,
          overflow: "hidden",
          zIndex: 2001,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }
        : {
          width: "340px",
          maxHeight: "620px",
          marginTop: "0.2rem",
          borderRadius: "0.3rem",
          border: `1px solid ${themePalette.border.main}`,
          boxShadow: themePalette.shadow.dialog,
          overflow: "hidden",
          zIndex: 2001,
        },
    [isMobile]
  )

  const contentAreaStyle = contentViewportHeight !== null
    ? {
      height: `${contentViewportHeight}px`,
      maxHeight: `${contentViewportHeight}px`,
    }
    : undefined

  const alertWrapperStyle = contentViewportHeight !== null
    ? { top: `${Math.round(topNavBottom)}px` }
    : undefined

  return (
    <Container>
      <Box ref={topNavRef}>
        <TopNav titulo="Dispositivos" marginLeft={true}>
          <Box className={classes.topNavSummaryWrapper}>
            <Box
              sx={{
                ...topNavSummaryCardSx,
                gap: isMobile ? "0.5rem" : topNavSummaryCardSx.gap,
                padding: isMobile ? "0.28rem 0.42rem" : topNavSummaryCardSx.padding,
              }}
            >
              <Box
                sx={{
                  ...planInfoContentSx,
                  width: "100%",
                  display: "flex",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  minWidth: 0,
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: isMobile ? "0.55rem" : planInfoContentSx.gap,
                }}
              >
                <span
                  style={{
                    ...planBadgeSx,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    fontSize: isMobile ? "0.68rem" : "0.78rem",
                    padding: isMobile ? "0.12rem 0.42rem" : planBadgeSx.padding,
                    minHeight: isMobile ? "1.8rem" : planBadgeSx.minHeight,
                  }}
                >
                  <CreditCardOutlined fontSize="inherit" style={{ fontSize: "0.92rem" }} />
                  {isMobile ? currentPlanLabel : `Plan actual: ${currentPlanLabel}`}
                </span>

                <Box
                  sx={{
                    ...topNavMetricsCardSx,
                    minHeight: isMobile ? "1.8rem" : topNavMetricsCardSx.minHeight,
                    padding: isMobile ? "0.12rem 0.5rem" : topNavMetricsCardSx.padding,
                    gap: isMobile ? "0.45rem" : topNavMetricsCardSx.gap,
                    flex: isMobile ? "0 0 auto" : "1 1 auto",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      ...planInfoValueSx,
                      whiteSpace: "nowrap",
                      fontSize: isMobile ? "0.72rem" : planInfoValueSx.fontSize,
                    }}
                  >
                    Dispositivos activos:{" "}
                    <span style={{ color: "rgba(255, 255, 255, 0.96)", fontWeight: 400 }}>{activeMetersCount}</span>
                    {" / -"}
                  </div>
                  {!isMobile && (
                    <Box sx={metersUsageTrackSx}>
                      <Box sx={metersUsageBarSx}>
                        <Box sx={metersUsageFillSx} />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </TopNav>
      </Box>

      {initialLoading && (
        <Box className={classes.initialLoading} style={contentAreaStyle}>
          <CircularProgress size={36} style={{ color: themePalette.primary.main }} />
        </Box>
      )}

      {!initialLoading && (
        <>
          <LoadingComponentBlur loadingState={loadingBlurState} />

          <Box className={classes.alertWrapper} style={alertWrapperStyle}>
            {close && (
              <Alert
                severity={msg.type}
                className={classes.alertInline}
                action={(
                  <IconButton
                    aria-label="close"
                    size="small"
                    onClick={() => {
                      setClose(false)
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                )}
              >
                {msg.msg}
              </Alert>
              )}
          </Box>

          <Box className={classes.contentArea} style={contentAreaStyle}>
            <LeftPanel
              isMobile={isMobile}
              mobileView={mobileView}
              leftPanelScrollRef={leftPanelScrollRef}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              activeFilterCount={activeFilterCount}
              handleOpenFilters={handleOpenFilters}
              filtersAnchorEl={filtersAnchorEl}
              handleCloseFilters={handleCloseFilters}
              filterPopoverPaperStyle={filterPopoverPaperStyle}
              filterSections={filterSections}
              selectedFilters={selectedFilters}
              toggleFilterValue={toggleFilterValue}
              clearAllFilters={clearAllFilters}
              filteredDevices={filteredDevices}
              selectedMeterId={selectedMeter?.id || null}
              setSelectedMeter={setSelectedMeter}
              setMobileView={setMobileView}
              renderCloudAccessBadge={renderCloudAccessBadge}
              renderAssignmentNodeIcon={renderAssignmentNodeIcon}
              expandedMeters={expandedMeters}
              setExpandedMeters={setExpandedMeters}
              isFilteringDevices={isFilteringDevices}
              getStatusIcon={renderStatusIcon}
            />

            <RightPanel
              isMobile={isMobile}
              mobileView={mobileView}
              setMobileView={setMobileView}
              rightPanelLoading={rightPanelLoading}
              selectedMeter={selectedMeter}
              editableName={editableName}
              setEditableName={setEditableName}
              handleNameBlur={handleNameBlur}
              loadingSave={loadingSave}
              loadingStateChange={loadingStateChange}
              selectedHasChildren={selectedHasChildren}
              handleMeterStatusChange={handleMeterStatusChange}
              selectedCloudSource={selectedCloudSource}
              selectedIsChild={selectedIsChild}
              shouldShowCloudActionButton={shouldShowCloudActionButton}
              selectedCloudButtonDisabled={selectedCloudButtonDisabled}
              renderCloudAccessBadge={renderCloudAccessBadge}
              handleCloudAccessAction={handleCloudAccessAction}
              openAffirmModal={openAffirmModal}
            />
          </Box>

          <ModalAffirm
            open={affirmModalState.open}
            title={affirmModalCopy.title}
            description={affirmModalCopy.description}
            confirmLabel={affirmModalCopy.confirmLabel}
            confirmColor={affirmModalCopy.confirmColor}
            onConfirm={handleAffirmAction}
            onClose={closeAffirmModal}
          />
        </>
      )}
    </Container>
  )
}
